import { useMemo } from 'react';
import { AppFlex } from 'shared/UI/UIKit/Flex';
import { AppInput } from 'shared/UI/UIKit/Input';
import { AppSelect } from 'shared/UI/UIKit/Select';
import { AppCheckIcon } from 'shared/UI/UIKit/icons/Check';
import { AppCloseIcon } from 'shared/UI/UIKit/icons/Close';
import { AppDeleteIcon } from 'shared/UI/UIKit/icons/Delete';
import { AppEditIcon } from 'shared/UI/UIKit/icons/Edit';
import type { AppTableColumn } from 'shared/UI/UIKit/Table';
import type { ClassGroup } from 'shared/api';
import type { useClassGroupsTable } from '../model';
import styles from './styles.module.scss';

type UseColumnsParams = ReturnType<typeof useClassGroupsTable>;

export const useColumns = ({ state, lookups, methods }: UseColumnsParams) =>
  useMemo<AppTableColumn<ClassGroup>[]>(
    () => [
      {
        key: 'educationalClassId',
        title: 'Класс',
        dataIndex: ['educationalClassId'],
        sorter: true,
        render: (id: number) => lookups.getClassName(id),
      },
      {
        key: 'subjectId',
        title: 'Предмет',
        dataIndex: ['subjectId'],
        sorter: true,
        render: (id: number) => lookups.getSubjectName(id),
      },
      {
        key: 'teacherId',
        title: 'Преподаватель',
        dataIndex: ['teacherId'],
        sorter: true,
        render: (teacherId: number | undefined, group: ClassGroup) =>
          state.editingGroup?.id === group.id ? (
            <div className={styles.editorContainer}>
              <AppSelect
                className={styles.editor}
                showSearch
                optionFilterProp="label"
                value={state.editTeacherId}
                loading={state.availableTeachersLoading}
                options={state.availableTeacherOptions}
                placeholder="Выберите преподавателя"
                onChange={(value) => methods.setEditTeacherId(value as number)}
              />
            </div>
          ) : (
            lookups.getTeacherName(teacherId)
          ),
      },
      {
        key: 'name',
        title: 'Название',
        dataIndex: ['name'],
        sorter: true,
        render: (name: string | undefined, group: ClassGroup) =>
          state.editingGroup?.id === group.id ? (
            <div className={styles.editorContainer}>
              <AppInput
                className={styles.editor}
                value={state.editName}
                maxLength={255}
                onChange={(event) => methods.setEditName(event.target.value)}
                onPressEnter={methods.saveEdit}
              />
            </div>
          ) : (
            name || '—'
          ),
      },
      {
        key: 'controls',
        title: '',
        dataIndex: [],
        width: '9rem',
        render: (_: unknown, group: ClassGroup) =>
          state.editingGroup?.id === group.id ? (
            <AppFlex direction="row" alignItems="center" className={styles.actions}>
              <AppCheckIcon
                role="button"
                aria-label="Сохранить"
                onClick={methods.saveEdit}
                className={state.isUpdating ? styles.disabled : undefined}
              />
              <AppCloseIcon role="button" aria-label="Отменить" onClick={methods.cancelEdit} />
            </AppFlex>
          ) : (
            <AppFlex direction="row" alignItems="center" className={styles.actions}>
              <AppEditIcon
                role="button"
                aria-label="Редактировать"
                onClick={() => methods.startEdit(group)}
              />
              <AppDeleteIcon
                role="button"
                aria-label="Удалить"
                onClick={() => methods.showDelete(group)}
              />
            </AppFlex>
          ),
      },
    ],
    [state, lookups, methods],
  );
